// Isometric projection. 2:1 diamond tiles (45° yaw / 30° pitch look), origin at
// tile (0,0)'s top corner, +x runs screen-right-down, +y screen-left-down.

export const TILE_WIDTH = 128;
export const TILE_HEIGHT = 64;
/** Vertical pixels per unit of tile elevation. */
export const TILE_DEPTH = 32;

export interface Point {
  x: number;
  y: number;
}

/** Tile space (fractional allowed) to world-space pixels. */
export function tileToWorld(tx: number, ty: number, tz = 0): Point {
  return {
    x: (tx - ty) * (TILE_WIDTH / 2),
    y: (tx + ty) * (TILE_HEIGHT / 2) - tz * TILE_DEPTH,
  };
}

/** Inverse of tileToWorld at a given elevation. Returns fractional tiles. */
export function worldToTile(wx: number, wy: number, tz = 0): Point {
  const y = wy + tz * TILE_DEPTH;
  const a = wx / (TILE_WIDTH / 2);
  const b = y / (TILE_HEIGHT / 2);
  return { x: (b + a) / 2, y: (b - a) / 2 };
}

/** Snaps a world-space point to the integer tile containing it. */
export function worldToTileSnapped(wx: number, wy: number, tz = 0): Point {
  const t = worldToTile(wx, wy, tz);
  return { x: Math.floor(t.x), y: Math.floor(t.y) };
}

export function isInsideGrid(
  tx: number,
  ty: number,
  width: number,
  height: number,
): boolean {
  return tx >= 0 && ty >= 0 && tx < width && ty < height;
}

/**
 * Painter's-algorithm depth key. Larger draws in front. Elevation outranks
 * ground position so a bird on a perch never sorts behind the perch.
 */
export function depthSort(tx: number, ty: number, tz = 0): number {
  return (tx + ty) * 16 + tz * 1024;
}

/** Corner points of a tile diamond, world space, clockwise from the top. */
export function tileDiamond(tx: number, ty: number, tz = 0): Point[] {
  const c = tileToWorld(tx, ty, tz);
  const hw = TILE_WIDTH / 2;
  const hh = TILE_HEIGHT / 2;
  return [
    { x: c.x, y: c.y },
    { x: c.x + hw, y: c.y + hh },
    { x: c.x, y: c.y + TILE_HEIGHT },
    { x: c.x - hw, y: c.y + hh },
  ];
}
